import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useTheme,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from "@mui/material";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function DeletionRequests() {
  const theme = useTheme();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; type: "process" | "reject" | ""; id: string }>({
    open: false,
    type: "",
    id: "",
  });

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${ipconfig}/admin/deletion-requests`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRequests(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching deletion requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (id: string) => {
    try {
      const response = await axios.put(
        `${ipconfig}/admin/process-deletion`,
        { requestId: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.data.status) {
        toast.success("Account deleted successfully");
        fetchRequests();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Error processing deletion");
    }
  };

  const handleReject = async (id: string) => {
    try {
      const response = await axios.put(
        `${ipconfig}/admin/reject-deletion`,
        { requestId: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.data.status) {
        toast.success("Request rejected");
        fetchRequests();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Error rejecting request");
    }
  };

  const triggerConfirm = (type: "process" | "reject", id: string) => {
    setConfirmDialog({
      open: true,
      type,
      id,
    });
  };

  const executeConfirm = () => {
    if (confirmDialog.type === "process") {
      handleProcess(confirmDialog.id);
    } else if (confirmDialog.type === "reject") {
      handleReject(confirmDialog.id);
    }
    setConfirmDialog({ open: false, type: "", id: "" });
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", p: 2, boxSizing: "border-box", gap: "20px" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
            User Deletion Requests
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Review and process user account removal requests
          </Typography>
        </Box>
      </Box>

      <Divider />

      {loading ? (
        <Spinner height="100%" />
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flexGrow: 1,
            borderRadius: "12px",
            borderColor: "divider",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "divider",
              borderRadius: "4px",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Email Address</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Mobile Number</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Requested On</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b", textAlign: "right" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ color: "text.secondary" }}>No pending requests found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req: any, idx) => (
                  <TableRow
                    key={req._id || idx}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b50",
                      },
                      transition: "all 0.15s ease",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{req.email}</TableCell>
                    <TableCell>{req.mobileNumber}</TableCell>
                    <TableCell>{new Date(req.createdAt).toLocaleString("en-IN")}</TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 700, fontSize: "11px", textTransform: "uppercase" }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: "right" }}>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => triggerConfirm("process", req._id)}
                        sx={{ mr: 1.5, borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
                      >
                        Process Deletion
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={() => triggerConfirm("reject", req._id)}
                        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
                      >
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: "", id: "" })}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700 }}>
          <WarningAmberIcon color="warning" />
          Confirm Request Action
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === "process"
              ? "Are you sure you want to PERMANENTLY delete this user account? All their synchronized client companies and licenses will be deleted from the database. This action is irreversible."
              : "Are you sure you want to reject this account deletion request?"}
          </DialogContentText>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button onClick={() => setConfirmDialog({ open: false, type: "", id: "" })} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={executeConfirm}
            variant="contained"
            color={confirmDialog.type === "process" ? "error" : "primary"}
            sx={{ textTransform: "none", fontWeight: 600, px: 3 }}
          >
            Confirm Action
          </Button>
        </DialogActions>
      </Dialog>
      
      <ToastContainer style={{ width: "auto" }} />
    </Box>
  );
}
